'use client'

import NextLink from 'next/link'
import { usePathname as useNextPathname } from 'next/navigation'
import { createContext, useContext, type ReactNode, type Ref } from 'react'

import { resolveFrameHref } from './href'

/**
 * Where the frame is rendered. In the host, basePath is empty and every link is
 * an app link. In a module that runs as its own app, Next prefixes every app
 * link with the module's basePath (/module/<id>); a link to the hub or the
 * administration must therefore be a plain anchor, and only links inside the
 * module stay app links.
 */
const FrameContext = createContext<{ basePath: string }>({ basePath: '' })

export function FrameProvider({ basePath, children }: { basePath: string; children: ReactNode }) {
  return <FrameContext.Provider value={{ basePath }}>{children}</FrameContext.Provider>
}

/** The current path as the browser shows it, basePath included. */
export function useFramePath(): string {
  const { basePath } = useContext(FrameContext)
  const path = useNextPathname()
  if (basePath === '') return path
  return path === '/' ? basePath : `${basePath}${path}`
}

export interface FrameLinkProps {
  href: string
  className?: string
  children?: ReactNode
  title?: string
  role?: string
  'aria-label'?: string
  'aria-current'?: 'page' | undefined
  onClick?: () => void
  ref?: Ref<HTMLAnchorElement>
  /** The target is another app: a plain anchor, never prefetched (FrameNavItem.document). */
  document?: boolean
}

/** A link that works from the host and from a module's own app alike. */
export function FrameLink({ href, document = false, ...props }: FrameLinkProps) {
  const { basePath } = useContext(FrameContext)
  // `document` matters in the host only: inside a module's own app its own
  // entry is an app link like any other, and a link out is a plain anchor anyway.
  if (document && basePath === '') return <a href={href} {...props} />
  const target = resolveFrameHref(basePath, href)
  return target.app ? <NextLink href={target.href} {...props} /> : <a href={target.href} {...props} />
}
